const CURRENCY_LOCALES: Record<string, string> = {
  AUD: "en-AU",
  NZD: "en-NZ",
  USD: "en-US",
  GBP: "en-GB",
  EUR: "de-DE",
  CAD: "en-CA",
  SGD: "en-SG",
  JPY: "ja-JP",
  CNY: "zh-CN",
};

export function getCurrencyLocale(currency: string): string {
  return CURRENCY_LOCALES[currency] ?? "en-NZ";
}

export function getCurrencySeparators(locale: string) {
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
  return {
    decimal: parts.find((part) => part.type === "decimal")?.value ?? ".",
    group: parts.find((part) => part.type === "group")?.value ?? ",",
  };
}

export function parseCurrencyInput(
  input: string,
  currency = "NZD",
  locale = getCurrencyLocale(currency),
): string {
  const { decimal, group } = getCurrencySeparators(locale);
  const escapedGroup = group.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const escapedDecimal = decimal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const normalized = input
    .replace(new RegExp(escapedGroup, "g"), "")
    .replace(new RegExp(escapedDecimal, "g"), ".")
    .replace(/[^\d.-]/g, "");
  const negative = normalized.startsWith("-");
  const unsigned = normalized.replace(/-/g, "");
  const [whole = "", ...fractionParts] = unsigned.split(".");
  const fraction = fractionParts.join("").slice(0, 2);
  const canonicalWhole = whole.replace(/^0+(?=\d)/, "") || "0";

  if (!whole && !fraction) return "";
  return `${negative ? "-" : ""}${canonicalWhole}${
    normalized.includes(".") ? `.${fraction}` : ""
  }`;
}

export function formatCurrencyInput(
  value: string | number | null | undefined,
  currency = "NZD",
  locale = getCurrencyLocale(currency),
): string {
  if (value === "" || value === null || value === undefined) return "";
  const amount = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(amount)) return "";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function getCurrencySymbol(
  currency = "NZD",
  locale = getCurrencyLocale(currency),
): string {
  return (
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
    })
      .formatToParts(0)
      .find((part) => part.type === "currency")?.value ?? currency
  );
}

export function formatCurrencyAmountInput(
  value: string | number | null | undefined,
  locale = "en-NZ",
): string {
  if (value === "" || value === null || value === undefined) return "";
  const amount = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(amount)) return "";
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatRegionalCurrency(
  amount: number,
  currency = "NZD",
  locale?: string,
) {
  return new Intl.NumberFormat(locale ?? getCurrencyLocale(currency), {
    style: "currency",
    currency,
  }).format(amount);
}
